import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { PlanChip } from "@/components/PlanChip";
import { useAuth } from "@/context/AuthContext";
import { apiCountries, apiUpdateProfile, apiDeleteAccount, apiBillingPortal } from "@/lib/namable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogOut, Crown, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const navigate = useNavigate();
  const { user, usage, logout, refreshUsage, setUser, ready } = useAuth();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(user?.country || "");
  const [portalLoading, setPortalLoading] = useState(false);

  const manageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await apiBillingPortal(user.user_id);
      window.location.href = portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal isn't available yet. Please contact support.");
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (ready && !user) navigate("/");
  }, [ready, user, navigate]);

  useEffect(() => {
    apiCountries().then((d) => setCountries((d.countries || []).filter((c) => c && c.code))).catch(() => {});
  }, []);

  const saveCountry = async (code) => {
    setCountry(code);
    try {
      await apiUpdateProfile({ country: code });
      setUser((u) => (u ? { ...u, country: code } : u));
      await refreshUsage();
      toast.success("Country updated — local domain extensions unlocked.");
    } catch (e) {
      toast.error("Couldn't update country.");
    }
  };

  const del = async () => {
    try {
      await apiDeleteAccount();
      setUser(null);
      toast.success("Account deleted.");
      navigate("/");
    } catch (e) {
      toast.error("Couldn't delete account.");
    }
  };

  if (!user) return null;
  const isPaid = usage?.is_paid;

  return (
    <div className="max-w-[560px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Seo title="Profile — Namable" description="Manage your Namable account and subscription." path="/profile" />
      <p className="overline text-brand">Account</p>
      <h1 className="font-display text-[30px] font-extrabold tracking-[-0.02em] text-ink mt-1.5">Profile</h1>

      {/* identity */}
      <div className="card-soft p-6 mt-7 flex items-center gap-4">
        <span className="w-14 h-14 rounded-full brand-gradient text-white flex items-center justify-center text-[22px] font-bold">
          {(user.name || user.email).slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="text-[17px] font-bold text-ink truncate">{user.name || "Founder"}</p>
          <p className="text-[13px] text-ink-subtle truncate">{user.email}</p>
        </div>
      </div>

      {/* plan */}
      <div className={`rounded-xl p-6 mt-4 ${isPaid ? "panel-dark" : "card-soft"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown size={18} className={isPaid ? "text-brand" : "text-ink-subtle"} />
            <span className={`font-bold text-[16px] ${isPaid ? "text-white" : "text-ink"}`}>
              {usage ? (usage.plan === "free" ? "Free plan" : `${usage.plan[0].toUpperCase()}${usage.plan.slice(1)} plan`) : "Plan"}
            </span>
          </div>
          {usage && !isPaid && <PlanChip usage={usage} />}
        </div>
        {usage && (
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div>
              <p className={`text-[22px] font-extrabold ${isPaid ? "text-white" : "text-ink"}`}>{usage.scans_remaining}</p>
              <p className={`text-[11px] ${isPaid ? "text-white/60" : "text-ink-subtle"}`}>scans left</p>
            </div>
            <div>
              <p className={`text-[22px] font-extrabold ${isPaid ? "text-white" : "text-ink"}`}>{usage.saved_count}/{usage.saved_max}</p>
              <p className={`text-[11px] ${isPaid ? "text-white/60" : "text-ink-subtle"}`}>saved</p>
            </div>
            <div>
              <p className={`text-[22px] font-extrabold ${isPaid ? "text-white" : "text-ink"}`}>{usage.allow_com ? "Yes" : "No"}</p>
              <p className={`text-[11px] ${isPaid ? "text-white/60" : "text-ink-subtle"}`}>.com search</p>
            </div>
          </div>
        )}
        {usage?.plan_platform && (
          <p className={`text-[12px] mt-4 ${isPaid ? "text-white/60" : "text-ink-subtle"}`}>
            Subscription via {usage.plan_platform === "web" ? "Stripe (web)" : usage.plan_platform}
          </p>
        )}
        {!isPaid && (
          <Button onClick={() => navigate("/pricing")} className="w-full mt-5 h-11 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow" data-testid="profile-upgrade">
            Upgrade plan
          </Button>
        )}
        {isPaid && (
          <Button
            onClick={manageSubscription}
            disabled={portalLoading}
            className="w-full mt-5 h-11 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
            data-testid="profile-manage-subscription"
          >
            {portalLoading ? <Loader2 className="animate-spin" size={16} /> : "Manage subscription"}
          </Button>
        )}
      </div>

      {/* country */}
      <div className="card-soft p-6 mt-4">
        <p className="text-[14px] font-bold text-ink flex items-center gap-2"><Globe size={16} /> Country</p>
        <p className="text-[13px] text-ink-subtle mt-1 mb-3">Unlocks your local domain extensions (e.g. .com.au, .co.uk).</p>
        <Select value={country || undefined} onValueChange={saveCountry}>
          <SelectTrigger className="h-11 rounded-lg" data-testid="country-select">
            <SelectValue placeholder="Select your country" />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>{c.name} {c.tld ? `(${c.tld})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* actions */}
      <div className="flex items-center justify-between mt-6">
        <Button variant="ghost" onClick={logout} className="text-ink-muted font-semibold" data-testid="profile-logout">
          <LogOut size={16} className="mr-2" /> Log out
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="text-[13px] font-semibold text-danger hover:underline" data-testid="delete-account-btn">Delete account</button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes your account, saved names and history. This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="rounded-lg bg-danger hover:bg-danger/90" data-testid="confirm-delete">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
